import {
  Box,
  Button,
  Grid,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { useStore } from "src/Shared/Hooks";
import { ToastService } from "src/Shared/Services";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";
import ActionModal from "src/Shared/Components/ActionModal/ActionModal";
import CompanyService from "../../Services/Company.service";
import { IOnboardingProgress } from "../../Interfaces/IOnboardingProgress";
import { IImperansSubmission } from "../../Interfaces/IImperansSubmission";
import { downloadJsonFile, formatLocalDeviceDateTime } from "../../Tools/OnboardingOutputs";
import { ONBOARDING_STEP_LABELS } from "../OnboardingWizard/OnboardingWizard";
import {
  AWAITS_IMPERANS_ACTIVATION_MESSAGE,
  awaitsImperansActivation,
} from "../OnboardingWizard/OnboardingSubmission.rules";
import {
  onboardingButtonLabel,
  resolveOnboardingCardMode,
} from "./OnboardingCard.utils";

/**
 * The Overview's onboarding presence (CR.2/CR.3): a full onboarding card with the
 * progress checklist for prospect-stage clients, a small audit card for onboarded
 * current/dormant clients, and nothing for clients that never submitted one.
 */
const OnboardingCard: React.FC = () => {
  const navigate = useNavigate();
  const {
    rootStore: {
      companyStore: { companyOverview: overview, markAsCurrentClient },
    },
  } = useStore();

  const [progress, setProgress] = useState<IOnboardingProgress | null>(null);
  // Mark as Current Client is an irreversible dual write, so it goes through the same confirmation
  // modal step 7's submitted panel already uses rather than firing straight off this button's click
  // (task 5835 — this card was the ungated one).
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);
  // Where the submitted onboarding stands with Imperans, read through the same rule step 7 uses:
  // once Imperans created the client as Draft, activation there promotes it (TC-6341-03),
  // so the card offers the promotion only after the answer is known.
  const [submission, setSubmission] = useState<IImperansSubmission | null>(null);
  const [submissionChecked, setSubmissionChecked] = useState(false);

  /** The JSON export regenerates on demand — identical apart from exportedAt (S7.14). */
  const downloadExport = (companyRowID: string) => {
    CompanyService.getOnboardingExport(companyRowID).subscribe({
      next: (exported) => downloadJsonFile(exported.fileName, exported.json),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  const mode = overview
    ? resolveOnboardingCardMode(overview.clientStatusID, overview.hasSubmittedOnboarding)
    : "none";

  useEffect(() => {
    if (mode !== "onboarding" || !overview?.companyRowID) return;
    CompanyService.getOnboardingProgress(overview.companyRowID).subscribe({
      next: (data) => setProgress(data),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  }, [mode, overview?.companyRowID, overview?.clientStatusID]);

  useEffect(() => {
    setSubmission(null);
    setSubmissionChecked(false);
    if (mode !== "onboarding" || !overview?.companyRowID || !overview.hasSubmittedOnboarding) return;
    CompanyService.getImperansSubmission(overview.companyRowID).subscribe({
      next: (data) => {
        setSubmission(data ?? null);
        setSubmissionChecked(true);
      },
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  }, [mode, overview?.companyRowID, overview?.hasSubmittedOnboarding]);

  if (!overview || mode === "none") return null;

  const submittedLine = overview.hasSubmittedOnboarding
    ? `Submitted ${
        overview.onboardingSubmittedOn
          ? formatLocalDeviceDateTime(overview.onboardingSubmittedOn)
          : ""
      }${overview.onboardingSubmittedBy ? ` by ${overview.onboardingSubmittedBy}` : ""}`
    : null;

  if (mode === "audit") {
    return (
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Onboarding record
        </Typography>
        <Typography variant="body2">{submittedLine}</Typography>
        {overview.imperansOnboardingLine && (
          <Typography variant="body2" color="text.secondary">
            {overview.imperansOnboardingLine}
          </Typography>
        )}
        {/* All three outputs stay reachable from the client record (S7.6). */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          <Button
            size="small"
            component={Link}
            to={`/company/${overview.companyRowID}/onboarding/sop`}
          >
            View SOP Output
          </Button>
          <Button
            size="small"
            component={Link}
            to={`/company/${overview.companyRowID}/onboarding/setup-copy`}
          >
            Setup copy view
          </Button>
          <Button size="small" onClick={() => downloadExport(overview.companyRowID)}>
            Download JSON
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Kept for historic auditing.
        </Typography>
      </Paper>
    );
  }

  const completed = progress?.completedSteps ?? 0;
  const total = progress?.totalSteps ?? 7;
  const nextStep = progress?.nextStep ?? 1;
  const isSubmitted = overview.hasSubmittedOnboarding;

  return (
    <>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Onboarding
            </Typography>
            {submittedLine && (
              <Typography variant="body2" color="text.secondary">
                {submittedLine}
              </Typography>
            )}
            {/* Created, rejected, activated: what Imperans did with it (Epic 6303, story 6341). */}
            {overview.imperansOnboardingLine && (
              <Typography variant="body2" color="text.secondary">
                {overview.imperansOnboardingLine}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                navigate(
                  `/company/${overview.companyRowID}/onboarding?step=${nextStep}`,
                )
              }
            >
              {onboardingButtonLabel(completed, isSubmitted)}
            </Button>
            {isSubmitted && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to={`/company/${overview.companyRowID}/onboarding/sop`}
                >
                  View SOP Output
                </Button>
                {/* The window straight after submission is exactly when the setup team needs the
                    copy view and the export; both used to appear only once the client had been
                    promoted to Current Client (bug 5710). */}
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to={`/company/${overview.companyRowID}/onboarding/setup-copy`}
                >
                  Setup copy view
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => downloadExport(overview.companyRowID)}
                >
                  Download JSON
                </Button>
                {/* Not offered while Imperans holds the client as Draft: activation arrives from
                    Imperans and moves the record itself (Feature CRM4), exactly as on step 7. */}
                {submissionChecked && !awaitsImperansActivation(submission) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPromoteOpen(true)}
                  >
                    Mark as Current Client
                  </Button>
                )}
                {submissionChecked && awaitsImperansActivation(submission) && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                    {AWAITS_IMPERANS_ACTIVATION_MESSAGE}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(completed / total) * 100}
            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2">
            {completed} of {total} steps complete
          </Typography>
        </Box>

        <List dense sx={{ mt: 1 }}>
          {ONBOARDING_STEP_LABELS.map((label, index) => (
            <ListItemButton
              key={label}
              component={Link}
              to={`/company/${overview.companyRowID}/onboarding?step=${index + 1}`}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                {progress?.stepsComplete?.[index] ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText primary={`Step ${index + 1} — ${label}`} />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Captured so far
          </Typography>
          {/* Section switches replace the history entry, like the sidebar (CR.9), so
              the panel changes in place and Back still leaves the record. */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              component={Link}
              replace
              to={`/company/${overview.companyRowID}/details`}
            >
              Edit in Details
            </Button>
            <Button
              size="small"
              component={Link}
              replace
              to={`/company/${overview.companyRowID}/company-contacts`}
            >
              Manage contacts
            </Button>
          </Box>
        </Box>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={6} md={3}>
            <Typography variant="h6">{overview.sitesCount}</Typography>
            <Typography variant="caption" color="text.secondary">
              Sites
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="h6">{overview.contactsCount}</Typography>
            <Typography variant="caption" color="text.secondary">
              Contacts
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            {/* Counted by the shared engine — each occasion once, however many methods cover
                it — so the card and the wizard always agree (CR.2). */}
            <Typography variant="h6">{overview.testingSelectionsCount ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">
              Testing selections
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            {/* The service line chosen on step 2, not the CRM TDDA/HealthTick field: the card has
                to read the same answer as steps 2 and 7 for the same client (CR.2, bug 5704). */}
            <Typography variant="h6" sx={{ fontSize: 18 }}>
              {overview.onboardingServiceLine || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Service line
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Same modal, same wording, as step 7's submitted panel (S7.7/task 5835) — this button
          performs the identical irreversible dual write, so it needs the identical confirmation. */}
      <ActionModal
        open={promoteOpen}
        title="Mark as Current Client?"
        items={[
          "Set this client's status to Current Client.",
          "Mark the client as linked to Imperans.",
          "Return you to Home with this client's row highlighted.",
        ]}
        confirmLabel="Mark as Current Client"
        busy={promoting}
        onCancel={() => setPromoteOpen(false)}
        onConfirm={() => {
          setPromoting(true);
          markAsCurrentClient(
            overview.companyRowID,
            () => {
              setPromoting(false);
              setPromoteOpen(false);
              navigate(`/company?focus=${overview.companyRowID}`);
            },
            () => setPromoting(false),
          );
        }}
      />
    </>
  );
};

export default observer(OnboardingCard);
